import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { uniqBy } from 'lodash';
import { useLazyQuery } from '@apollo/client';
import { CheckboxesOptions } from '../../../../../../../components/checkbox-select';
import { PHASES_COLORS } from '../../../../../../../const';
import { useInstallationContext } from '../../../../../../../context/installation';
import {
  AggregateVectorParameterTypeInternal,
  ChannelTypeInternal,
  MeasurementChannelKind,
  MeterTypeInternal,
  PersistedMeterAggregatedMeasurementsQueryVariables,
  Query,
  TariffKindInternal,
} from '../../../../../../../data-access/gql-types/graphql';
import { PERSISTED_METER_AGGREGATED_MEASUREMENTS } from '../../../../../../../data-access/queries/meter';
import { MeasurementRange } from '../../../../../../../types';
import { convertDateToString } from '../../../../../../../utils/helpers/date-convert';
import { useMeasurementMapOperations } from '../../../../../hooks/measurement-map/use-measurement-map-operations';
import * as storage from '../../../../../storage';
import { ChannelMeterType } from '../../../../../types';
import { useMeterDetailsContext } from '../../context';
import { AnalysisHookParams, ChartDataItem } from '../types';
import {
  chartParameters,
  currentFormat,
  getCheckedParameter,
  getParameterColor,
  prepareParametersForRequest,
  priceAndCostParameters,
} from '../utils/helpers';
import { getDateRangeByPage, getRangeLabel } from '../utils/range';

export const useMeterAnalysisPersisted = ({ channel }: AnalysisHookParams) => {
  const { t } = useTranslation('channel-details');
  const { selectedInstallationId } = useInstallationContext();
  const { convertMeasurementToNumber, measurementMap } = useMeasurementMapOperations();
  const {
    aggregatedParameters,
    setAggregatedParameters,
    activeMeasurementRange,
    setActiveMeasurementRange,
    analysisPhases: selectedPhases,
    setAnalysisPhases: setSelectedPhases,
  } = useMeterDetailsContext();
  const [page, setPage] = useState<number>(0);
  const [getPersistedAggregatedMeasurements, { data, loading }] = useLazyQuery<
    Query,
    PersistedMeterAggregatedMeasurementsQueryVariables
  >(PERSISTED_METER_AGGREGATED_MEASUREMENTS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const channelData = channel?.data as ChannelMeterType | undefined;
  const tariffKind = channelData?.tariff?.kind;
  const supported = channelData?.supportedAggregateVectorParameters || [];

  const isStandalone =
    channelData?.type === ChannelTypeInternal.Meter && channelData?.meterType === MeterTypeInternal.Standalone;

  const defaultPhases = isStandalone
    ? [0, 1, 2, 3]
    : [0, ...(channel.data as ChannelMeterType).measuredChannels.map((x) => x.phase)];

  const checkIfSupported = (param: AggregateVectorParameterTypeInternal) => {
    return supported.includes(convertMeasurementToNumber(MeasurementChannelKind.Meter)('aggregates', param));
  };

  const fromTo = useMemo(() => getDateRangeByPage(activeMeasurementRange, page), [activeMeasurementRange, page]);

  useEffect(() => {
    if (supported.length && !aggregatedParameters.length && measurementMap) {
      setAggregatedParameters(
        chartParameters.map(({ labelKey, type }) => ({
          label: t(`energyMeasurements.types.summary.${labelKey}`),
          value: type,
          checked: getCheckedParameter(type),
          disabled: !checkIfSupported(type),
        })),
      );
    }
  }, [supported, t, convertMeasurementToNumber, measurementMap, aggregatedParameters, tariffKind]);

  useEffect(() => {
    const aggregateVectorRequestItems = prepareParametersForRequest(
      aggregatedParameters,
      activeMeasurementRange,
      checkIfSupported,
    );

    getPersistedAggregatedMeasurements({
      variables: {
        installationId: selectedInstallationId,
        input: {
          channelId: channel.id,
          deviceId: channel.deviceId,
          indices: selectedPhases.length === 1 && selectedPhases[0] === 0 ? selectedPhases : [0, ...selectedPhases],
          aggregateVectorRequestItems,
          from: fromTo.from,
          to: fromTo.to,
        },
      },
    });
  }, [aggregatedParameters, activeMeasurementRange, supported, selectedPhases, fromTo]);

  const onClickActiveMeasurementRange = (value: MeasurementRange) => {
    setActiveMeasurementRange(value);
    setPage(0);

    setAggregatedParameters((prev) => {
      return prev.map((x) => {
        if (value === MeasurementRange.DayInHours) {
          return {
            ...x,
            checked: priceAndCostParameters.includes(x.value) ? false : x.checked,
            disabled: priceAndCostParameters.includes(x.value)
              ? !tariffKind || tariffKind === TariffKindInternal.None
              : x.disabled,
          };
        }

        return {
          ...x,
          checked: priceAndCostParameters.includes(x.value) ? false : x.checked,
          disabled: priceAndCostParameters.includes(x.value) ? true : x.disabled,
        };
      });
    });
  };

  const handleSetAggregatedParameters = (options: CheckboxesOptions<AggregateVectorParameterTypeInternal>[]) => {
    setSelectedPhases([0]);
    setAggregatedParameters(options);

    const checkedParameters = options.filter((x) => x.checked).map((x) => x.value);
    storage.setItem('meterParameters', checkedParameters);
  };

  const error = data?.persistedMeterAggregatedMeasurements.error?.errorCode;
  const noData = !data?.persistedMeterAggregatedMeasurements.data?.items.length;

  const chartData: ChartDataItem[] = useMemo(() => {
    const items = data?.persistedMeterAggregatedMeasurements.data?.items || [];

    return uniqBy(
      items.map((item) => {
        const date = new Date(item.dateAtUtc);
        const measurements = item.measurements as Record<AggregateVectorParameterTypeInternal, Record<number, number>>;

        return {
          date: date.getTime(),
          time: convertDateToString(date.getTime(), currentFormat(false, activeMeasurementRange)),
          tooltipTime: convertDateToString(date.getTime(), currentFormat(true, activeMeasurementRange)),
          values: Object.entries(measurements)
            .flatMap(([parameter, indicesObj]) => {
              const [measuerementType, _] = parameter.split('@');
              const type = measuerementType as AggregateVectorParameterTypeInternal;

              return Object.entries(indicesObj).map(([indice, value]) => {
                const index = Number(indice);
                const predictedCost =
                  [
                    AggregateVectorParameterTypeInternal.Cost,
                    AggregateVectorParameterTypeInternal.VectorBalancedCost,
                  ].includes(type) &&
                  measurements[AggregateVectorParameterTypeInternal.ForwardActiveEnergy]?.[index] == null;

                return {
                  type,
                  index,
                  value: [AggregateVectorParameterTypeInternal.Price].includes(type) ? value : value / 1000,
                  color: index ? PHASES_COLORS[index] : getParameterColor(type),
                  ...(predictedCost && { data: { predictedCost } }),
                };
              });
            })
            .filter((x) =>
              selectedPhases.length === 1 && selectedPhases[0] === 0
                ? x.index === 0
                : [...selectedPhases].includes(x.index) || (x.index === 0 && priceAndCostParameters.includes(x.type)),
            ),
        };
      }),
      (x) => x.tooltipTime,
    );
  }, [data?.persistedMeterAggregatedMeasurements.data?.items, fromTo]);

  const currentRangeLabel = getRangeLabel(activeMeasurementRange, fromTo);

  const isPreviousData = useMemo(() => {
    const persistedData = data?.persistedMeterAggregatedMeasurements.data;
    if (persistedData) {
      if (!persistedData.oldestAtUtc) return true;

      const from = new Date(persistedData.from);
      const oldestAtUtc = new Date(persistedData.oldestAtUtc);

      return oldestAtUtc < from;
    }

    return false;
  }, [data?.persistedMeterAggregatedMeasurements]);

  return {
    chartData,
    currentRangeLabel,
    isLoading: loading && !data,
    selectedPhases,
    fromTo,
    setSelectedPhases,
    onClickActiveMeasurementRange,
    handleSetAggregatedParameters,
    aggregatedParameters,
    activeMeasurementRange,
    page,
    setPage,
    isStandalone,
    defaultPhases,
    error,
    isPreviousData,
    noData,
  };
};
